import { useState, useEffect, useRef } from "react";
import { Input, Button, Tag, Spin, Popconfirm } from "antd";
import { PaperClipOutlined, SendOutlined, CloseCircleOutlined, DownloadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  TicketResponse,
  MessageResponse,
  getMessages,
  getTicket,
  sendMessage,
  downloadAttachment,
  downloadAllMessageAttachments,
  changeTicketStatus,
} from "../helpCenter.service";
import TicketStatusBadge from "../components/TicketStatusBadge";
import { normalizeRole } from "../../../utils/roles";

const getCurrentUserRole = (): string => {
  try {
    const u = localStorage.getItem("user");
    if (!u) return "";
    const parsed = JSON.parse(u);
    return parsed?.roles?.[0]?.role_name ?? "";
  } catch {
    return "";
  }
};

type Props = {
  ticket: TicketResponse;
  onBack: () => void;
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const currentUsername = (): string => {
  try {
    const u = localStorage.getItem("user");
    return u ? JSON.parse(u).username ?? "" : "";
  } catch {
    return "";
  }
};

const TicketPreviewPage = ({ ticket, onBack }: Props) => {
  const { t } = useTranslation("helpCenter");
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [ticketStatus, setTicketStatus] = useState(ticket.status);
  const [resolving, setResolving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const isAdmin = normalizeRole(getCurrentUserRole()) === "owner";

  const handleResolve = async () => {
    setResolving(true);
    try {
      const updated = await changeTicketStatus(ticket.id, "resolved");
      setTicketStatus(updated.status);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail || t("preview.resolve.error"),
      );
    } finally {
      setResolving(false);
    }
  };

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setMessagesLoading(true);
    getMessages(ticket.id)
      .then((data) => {
        if (!cancelled) setMessages(Array.isArray(data) ? data : []);
      })
      .finally(() => {
        if (!cancelled) setMessagesLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticket.id]);

  // Poll every 15s for new messages and status (support team replies arrive via email)
  useEffect(() => {
    if (ticketStatus === "resolved") return;
    const interval = setInterval(async () => {
      try {
        const data = await getMessages(ticket.id);
        setMessages(Array.isArray(data) ? data : []);
      } catch {}
      try {
        const updated = await getTicket(ticket.id);
        setTicketStatus(updated.status);
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [ticket.id, ticketStatus]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!replyText.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      const msg = await sendMessage(ticket.id, replyText, pendingFiles);
      setMessages((prev) => [...prev, msg]);
      setReplyText("");
      setPendingFiles([]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail || t("preview.chat.sendError"),
      );
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPendingFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSend();
    }
  };

  const me = currentUsername();

  return (
    <div className="layout-style" style={{ marginLeft: 0 }}>
      <div className="tickets-card">
        <div className="tickets-card-header">
          <h2>{t("preview.title")}</h2>
        </div>

        <button className="back-link" onClick={onBack}>
          ← {t("preview.goBack")}
        </button>

        <div className="ticket-preview-grid">
          {/* Left: Ticket summary */}
          <aside className="ticket-summary-card">
            <div className="ticket-summary-header">{t("preview.summary.header")}</div>

            <div className="ticket-summary-content">
              <div className="summary-item">
                <span className="summary-label">{t("preview.summary.status")}</span>
                <TicketStatusBadge status={ticketStatus} />
              </div>

              {isAdmin && ticketStatus !== "resolved" && (
                <Popconfirm
                  title={t("preview.resolve.confirmTitle")}
                  onConfirm={handleResolve}
                  okText={t("preview.resolve.confirmOk")}
                  cancelText={t("preview.resolve.confirmCancel")}
                >
                  <Button
                    icon={<CheckCircleOutlined />}
                    loading={resolving}
                    className="resolve-btn"
                  >
                    {t("preview.resolve.button")}
                  </Button>
                </Popconfirm>
              )}

              {ticket.email && (
                <div className="summary-item">
                  <span className="summary-label">{t("preview.summary.userEmail")}</span>
                  <strong>{ticket.email}</strong>
                </div>
              )}

              <div className="summary-item">
                <span className="summary-label">{t("preview.summary.type")}</span>
                <Tag className="ticket-type-tag">{ticket.ticket_type}</Tag>
              </div>

              <div className="summary-item">
                <span className="summary-label">{t("preview.summary.subject")}</span>
                <p className="summary-description">
                  {ticket.subject || ticket.description}
                </p>
              </div>

              <div className="summary-item">
                <span className="summary-label">{t("preview.summary.creationDate")}</span>
                <strong>{formatDate(ticket.created_at)}</strong>
              </div>
            </div>
          </aside>

          {/* Right: Chat */}
          <section className="ticket-chat-card">
            <div className="ticket-chat-header">
              <span className="chat-header-avatar">
                {ticket.created_by_username.charAt(0).toUpperCase()}
              </span>
              <strong>{ticket.created_by_username}</strong>
            </div>

            <div className="ticket-chat-body">
              {messagesLoading ? (
                <div className="chat-loading">
                  <Spin />
                </div>
              ) : messages.length === 0 ? (
                <p className="chat-empty">{t("preview.chat.noMessages")}</p>
              ) : (
                messages.map((msg) => {
                  const isSupport = msg.user_id === null;
                  const isMe = !isSupport && msg.username === me;
                  return (
                    <div
                      key={msg.id}
                      className={`message ${isMe ? "message-right" : "message-left"}`}
                    >
                      <div className="message-meta">
                        {!isMe && (
                          <span className={`avatar-small ${isSupport ? "avatar-support" : ""}`}>
                            {isSupport ? "S" : (msg.username?.charAt(0).toUpperCase() ?? "?")}
                          </span>
                        )}
                        <span>
                          {isSupport ? t("preview.chat.supportTeam") : msg.username} &middot; {formatDateTime(msg.created_at)}
                        </span>
                      </div>

                      <div
                        className={`message-bubble ${isMe ? "admin-bubble" : ""}`}
                      >
                        {msg.content && <p>{msg.content}</p>}

                        {msg.attachments.length > 0 && (
                          <div className="message-attachments">
                            {msg.attachments.map((att) => (
                              <button
                                key={att.id}
                                className="attachment-card"
                                onClick={() =>
                                  downloadAttachment(
                                    ticket.id,
                                    att.id,
                                    att.file_name,
                                  )
                                }
                              >
                                <PaperClipOutlined />
                                <div className="attachment-info">
                                  <strong>{att.file_name}</strong>
                                  <span>{formatFileSize(att.file_size)}</span>
                                </div>
                              </button>
                            ))}
                            {msg.attachments.length > 1 && (
                              <button
                                className="download-all-link"
                                onClick={() =>
                                  downloadAllMessageAttachments(
                                    ticket.id,
                                    msg.id,
                                  )
                                }
                              >
                                <DownloadOutlined /> {t("preview.chat.downloadAll")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatBottomRef} />
            </div>

            {/* Pending files preview */}
            {pendingFiles.length > 0 && (
              <div className="pending-files">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="pending-file-chip">
                    <PaperClipOutlined />
                    <span>{f.name}</span>
                    <button onClick={() => removeFile(i)}>
                      <CloseCircleOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Reply box */}
            <div className="ticket-reply-box">
              <Input.TextArea
                rows={2}
                placeholder={t("preview.chat.replyPlaceholder")}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf,.pdf"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <Button
                icon={<PaperClipOutlined />}
                onClick={() => fileInputRef.current?.click()}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                onClick={handleSend}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TicketPreviewPage;
